/**
 * @file The developer window (issue #56). One key, one window, every tool.
 *
 * Debug affordances used to be scattered: `*` drew the bomber overlay, another
 * `*` listener inside {@link ../systems/EnemyRangedAISystem.js} drew vision
 * cones, `ñ` opened the wave sandbox, and the spawn sandbox had no way in at
 * all. None of them were discoverable from inside the game.
 *
 * This window is the single entry point. It owns nothing itself: it hosts the
 * existing tools as tabs and flips the switches they expose, so adding a tool
 * is a `tabs` entry here plus whatever that tool already is.
 *
 * Like the HUD it is DOM rather than in-scene, and like the wave sandbox it is
 * built on first open and inert while closed.
 */

import { keyLabelsFor, Action } from '../input/InputManager.js';
import { Health, PlayerControlled, StatusEffects, Transform, Velocity, Weapon } from '../components/index.js';
import { Events } from '../core/EventBus.js';
import { DebugOverlaySystem } from '../systems/DebugOverlaySystem.js';
import { EnemyRangedAISystem } from '../systems/EnemyRangedAISystem.js';
import { resetDebugPrefs, saveDebugPrefsFrom } from '../config/DebugPrefs.js';
import { resetLevelLab } from '../config/LevelLab.js';
import { inspectCameraTuning, resetCameraTuning, saveCameraTuning, setCameraValue } from '../config/CameraTuning.js';
import { getPlayerTuning, resetPlayerTuning, savePlayerTuning, setPlayerValue } from '../config/PlayerTuning.js';
import { inspectWeaponTuning, resetWeaponTuning, saveWeaponTuning, setWeaponValue } from '../config/WeaponTuning.js';
import { resetWaveTuning } from '../config/WaveTuning.js';
import { inspectEnemyTuning, resetEnemyTuning, saveEnemyTuning, setEnemyValue } from '../config/EnemyTuning.js';
import { equipWeaponOn } from '../systems/InteractionSystem.js';
import { resetSandboxState, sandboxState } from '../sandbox/SandboxConfig.js';
import { HUD_SCALE_CONTROLS } from './HUD.js';

/** Seconds between player stat refreshes while the window is open. */
const PLAYER_STATS_INTERVAL = 0.1;

/**
 * Overlay switches rendered on the first tab. Each one reads and writes a
 * system, so the window holds no duplicate state that could drift.
 * @type {readonly { id: string, label: string, hint: string, system: Function,
 *   get: (system: any) => boolean, set: (system: any, on: boolean) => void }[]}
 */
const TOGGLES = Object.freeze([
  {
    id: 'bombers',
    label: 'Bomber AI overlay',
    hint: 'Detection cones, line of sight, blast rings and a live state readout.',
    system: DebugOverlaySystem,
    get: (system) => system.debugVisible,
    set: (system, on) => system.setDebugVisible(on)
  },
  {
    id: 'ranged',
    label: 'Ranged vision cones',
    hint: 'Per-shooter cone showing what each ranged enemy can currently see.',
    system: EnemyRangedAISystem,
    get: (system) => system.debugVisible,
    set: (system, on) => system.setDebugVisible(on)
  }
]);

export class DebugPanel {
  /**
   * @param {HTMLElement} root Container element; the window appends itself to it.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @param {object} [tools] Tools to host. Any of them may be absent.
   * @param {import('./WaveDebugPanel.js').WaveDebugPanel} [tools.waveDebug] Wave sandbox.
   * @param {{ setEnabled: (on: boolean) => void, update: (dt: number) => void }} [tools.sandbox]
   *   Spawn sandbox, already built into this window's sandbox tab.
   * @param {import('./HUD.js').HUD} [tools.hud] Gameplay HUD scale surface.
   * @param {() => void} [tools.onRestartLevel] Restarts the current level.
   * @param {() => boolean} [tools.canRestartLevel] Whether the restart button is live.
   */
  constructor(
    root,
    ctx,
    { waveDebug = null, sandbox = null, hud = null, onRestartLevel = null, canRestartLevel = null } = {}
  ) {
    /** @type {HTMLElement} @private */
    this._root = root;
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {import('./WaveDebugPanel.js').WaveDebugPanel | null} @private */
    this._waveDebug = waveDebug;
    /** @type {any} @private */
    this._sandbox = sandbox;
    /** @type {import('./HUD.js').HUD | null} @private */
    this._hud = hud;
    /** @type {(() => void) | null} @private */
    this._onRestartLevel = onRestartLevel;
    /** @type {(() => boolean) | null} @private */
    this._canRestartLevel = canRestartLevel;
    /** @type {import('./LevelLabPanel.js').LevelLabPanel | null} @private */
    this._levelLab = null;
    /** @type {import('./PostFxPanel.js').PostFxPanel | null} @private */
    this._postFx = null;
    /** @type {HTMLElement | null} Built on first open. @private */
    this._el = null;
    /** @type {Map<string, HTMLElement>} Tab id -> panel body. @private */
    this._panels = new Map();
    /** @type {Map<string, HTMLElement>} Tab id -> tab button. @private */
    this._tabs = new Map();
    /** @type {Map<string, HTMLInputElement>} `${weapon}:${path}` -> input. @private */
    this._weaponInputs = new Map();
    /** @type {boolean} @private */
    this._weaponsBuilt = false;
    /** @type {Map<string, HTMLInputElement>} `${enemy}:${path}` -> input. @private */
    this._enemyInputs = new Map();
    /** @type {boolean} @private */
    this._enemyBuilt = false;
    /** @type {Map<string, HTMLInputElement>} Camera path -> input. @private */
    this._cameraInputs = new Map();
    /** @type {boolean} @private */
    this._cameraBuilt = false;
    /** @type {string} @private */
    this._active = '';
    /** @type {boolean} @private */
    this._open = false;
    /** @type {number} @private */
    this._sincePlayerStats = 0;
  }

  /**
   * Hands the window its tools. Separate from the constructor because the tools
   * are built *into* this window's tab bodies, so they cannot exist yet when it
   * is constructed.
   *
   * @param {object} tools Tools to host. Any of them may be absent.
   * @param {import('./WaveDebugPanel.js').WaveDebugPanel} [tools.waveDebug] Wave sandbox.
   * @param {{ setEnabled: (on: boolean) => void }} [tools.sandbox] Spawn sandbox.
   * @param {import('./LevelLabPanel.js').LevelLabPanel} [tools.levelLab] Level lab.
   * @param {import('./PostFxPanel.js').PostFxPanel} [tools.postFx] Pulse post-FX tab.
   * @param {import('./HUD.js').HUD} [tools.hud] Gameplay HUD scale surface.
   * @param {() => void} [tools.onRestartLevel] Restarts the current level.
   * @param {() => boolean} [tools.canRestartLevel] Whether the restart button is live.
   * @returns {void}
   */
  attach({
    waveDebug = null,
    sandbox = null,
    levelLab = null,
    postFx = null,
    hud = null,
    onRestartLevel = null,
    canRestartLevel = null
  } = {}) {
    if (waveDebug) this._waveDebug = waveDebug;
    if (sandbox) this._sandbox = sandbox;
    if (levelLab) this._levelLab = levelLab;
    if (postFx) this._postFx = postFx;
    if (hud) this._hud = hud;
    if (onRestartLevel) this._onRestartLevel = onRestartLevel;
    if (canRestartLevel) this._canRestartLevel = canRestartLevel;
  }

  /** @returns {boolean} True while the window is on screen. */
  get isOpen() {
    return this._open;
  }

  /** @returns {string} Id of the visible tab, or `''` while closed. */
  get activeTab() {
    return this._active;
  }

  /**
   * The host element the spawn sandbox must be built into. Available before the
   * window is open, because the sandbox builds its panel up front.
   * @returns {HTMLElement} The sandbox tab body.
   */
  get sandboxHost() {
    if (!this._el) this._build();
    return this._panels.get('sandbox');
  }

  /**
   * The host element the wave sandbox must be built into.
   * @returns {HTMLElement} The waves tab body.
   */
  get waveHost() {
    if (!this._el) this._build();
    return this._panels.get('waves');
  }

  /**
   * The host element the level lab must be built into.
   * @returns {HTMLElement} The levels tab body.
   */
  get levelsHost() {
    if (!this._el) this._build();
    return this._panels.get('levels');
  }

  /**
   * The host element the Post FX tab must be built into.
   * @returns {HTMLElement} The Post FX tab body.
   */
  get postfxHost() {
    if (!this._el) this._build();
    return this._panels.get('postfx');
  }

  /**
   * Reopens the window exactly as a previous session left it.
   * @param {{ open?: boolean, tab?: string }} saved Persisted hub state.
   * @returns {void}
   */
  restore(saved) {
    if (!saved) return;
    if (saved.tab) this._active = saved.tab;
    if (saved.open) this.open();
  }

  /** @returns {void} Shows or hides the window. */
  toggle() {
    this._open ? this.close() : this.open();
  }

  /** @returns {void} */
  open() {
    if (!this._el) this._build();
    this._open = true;
    this._el.hidden = false;
    this._syncToggles();
    this._syncPlayerCheats();
    this._buildWeaponSelect();
    this._syncWeaponSelect();
    this._syncUiScales();
    this._syncUiCopy();
    this._syncPulseMax();
    this._syncCameraTuning();
    this._syncWeaponTuning();
    this._syncEnemyTuning();
    this._syncRunActions();
    this._sincePlayerStats = PLAYER_STATS_INTERVAL;
    this._refreshPlayerStats(this._ctx);
    this._selectTab(this._active || 'overlays');
  }

  /** @returns {void} */
  close() {
    this._open = false;
    saveDebugPrefsFrom({ hub: { open: false, tab: this._active } });
    if (this._el) this._el.hidden = true;
    // Tools that own input or per-frame work stop with the window; overlays
    // stay as the developer left them, since watching them *while playing* is
    // the whole point of the switches.
    this._waveDebug?.close();
    this._sandbox?.setEnabled(false);
    this._levelLab?.setVisible(false);
  }

  /**
   * Per-frame tick. Returns immediately while closed.
   *
   * @param {number} dt Delta time in seconds.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   */
  update(dt, ctx) {
    if (!this._open) return;
    this._waveDebug?.update(dt, ctx);
    this._sincePlayerStats += dt;
    if (this._sincePlayerStats >= PLAYER_STATS_INTERVAL) {
      this._sincePlayerStats = 0;
      this._refreshPlayerStats(ctx);
      this._syncWeaponSelect();
      this._syncRunActions();
      if (this._active === 'ui') {
        this._syncUiScales();
      }
      if (this._active === 'overlays') this._syncPulseMax();
      if (this._active === 'camera') this._syncCameraTuning();
    }
  }

  /** @returns {void} @private */
  _build() {
    const panel = document.createElement('div');
    panel.className = 'debug-hub';
    panel.hidden = true;

    const key = keyLabelsFor(Action.TOGGLE_DEBUG)[0] ?? ',';
    panel.innerHTML = `
      <header class="debug-hub__head">
        <span class="debug-hub__title">Debug</span>
        <span class="debug-hub__key"><kbd>${key}</kbd></span>
        <button type="button" class="debug-hub__close" data-dbg="close" title="Close">×</button>
      </header>
      <nav class="debug-hub__tabs" data-dbg="tabs">
        <button type="button" class="debug-hub__tab" data-tab="overlays">General</button>
        <button type="button" class="debug-hub__tab" data-tab="ui">UI</button>
        <button type="button" class="debug-hub__tab" data-tab="camera">Camera</button>
        <button type="button" class="debug-hub__tab" data-tab="weapons">Weapons</button>
        <button type="button" class="debug-hub__tab" data-tab="enemies">Enemies</button>
        <button type="button" class="debug-hub__tab" data-tab="waves">Waves</button>
        <button type="button" class="debug-hub__tab" data-tab="sandbox">Sandbox</button>
        <button type="button" class="debug-hub__tab" data-tab="levels">Levels</button>
        <button type="button" class="debug-hub__tab" data-tab="postfx">Post FX</button>
      </nav>
      <div class="debug-hub__body">
        <section class="debug-hub__panel" data-panel="overlays">
          ${TOGGLES.map(
            (toggle) => `
              <label class="debug-hub__switch">
                <input type="checkbox" data-toggle="${toggle.id}" />
                <span>
                  <b>${toggle.label}</b>
                  <em>${toggle.hint}</em>
                </span>
              </label>`
          ).join('')}
          <section class="debug-hub__section">
            <h4 class="debug-hub__heading">Player stats</h4>
            <div class="debug-hub__stats" data-dbg="playerStats">
              <div><span>Move speed</span><b data-dbg-stat="moveSpeed">--</b></div>
              <div><span>Pulse</span><b data-dbg-stat="pulse">--</b></div>
              <div><span>I-frames</span><b data-dbg-stat="iframes">--</b></div>
              <div><span>Shield</span><b data-dbg-stat="shield">--</b></div>
              <div><span>Position</span><b data-dbg-stat="position">--</b></div>
            </div>
          </section>
          <section class="debug-hub__section">
            <h4 class="debug-hub__heading">Pulse tuning</h4>
            <label class="debug-hub__scale-row">
              <span class="debug-hub__scale-copy">
                <b>Pulse max</b>
                <em>Maximum countdown seconds.</em>
              </span>
              <input type="range" min="1" max="60" step="1" data-player-param="killTimerMax" />
              <input type="number" min="1" max="60" step="1" data-player-param="killTimerMax" />
            </label>
          </section>
          <section class="debug-hub__section">
            <h4 class="debug-hub__heading">Player cheats</h4>
            <div class="debug-hub__cheats">
              <label class="debug-hub__switch">
                <input type="checkbox" data-toggle="immortal" />
                <span>
                  <b>Immortal</b>
                  <em>Takes no damage; pulse still drains.</em>
                </span>
              </label>
              <label class="debug-hub__switch">
                <input type="checkbox" data-toggle="pulseFrozen" />
                <span>
                  <b>Pulse frozen</b>
                  <em>Kill timer stops; damage still counts.</em>
                </span>
              </label>
              <div class="debug-hub__weapon-select">
                <label for="debug-weapon-select">Equip weapon</label>
                <select id="debug-weapon-select" data-debug="equipWeapon"></select>
              </div>
            </div>
          </section>
          <section class="debug-hub__section">
            <h4 class="debug-hub__heading">Level tools</h4>
            <button type="button" class="debug-hub__button debug-hub__button--danger"
                    data-debug="restartLevel">Restart level</button>
            <button type="button" class="debug-hub__button debug-hub__button--danger"
                    data-debug="resetSavedDebug">Reset saved debug</button>
          </section>
        </section>
        <section class="debug-hub__panel" data-panel="ui" hidden></section>
        <section class="debug-hub__panel" data-panel="camera" hidden></section>
        <section class="debug-hub__panel" data-panel="weapons" hidden></section>
        <section class="debug-hub__panel" data-panel="enemies" hidden></section>
        <section class="debug-hub__panel" data-panel="waves" hidden></section>
        <section class="debug-hub__panel" data-panel="sandbox" hidden></section>
        <section class="debug-hub__panel" data-panel="levels" hidden></section>
        <section class="debug-hub__panel" data-panel="postfx" hidden></section>
      </div>
    `;

    this._root.appendChild(panel);
    this._el = panel;

    for (const node of panel.querySelectorAll('[data-panel]')) {
      this._panels.set(/** @type {HTMLElement} */ (node).dataset.panel, /** @type {HTMLElement} */ (node));
    }
    for (const node of panel.querySelectorAll('[data-tab]')) {
      const tab = /** @type {HTMLElement} */ (node);
      this._tabs.set(tab.dataset.tab, tab);
      tab.addEventListener('click', () => this._selectTab(tab.dataset.tab));
    }

    panel.querySelector('[data-dbg="close"]').addEventListener('click', () => this.close());
    this._buildUiPanel();
    this._buildCameraTuningPanel();
    this._buildWeaponTuningPanel();
    this._buildEnemyTuningPanel();

    panel.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const button = /** @type {HTMLButtonElement | null} */ (target.closest?.('[data-debug]'));
      if (!button || button.disabled) return;
      if (button.dataset.debug === 'restartLevel') {
        this._onRestartLevel?.();
        this._syncRunActions();
      } else if (button.dataset.debug === 'resetSavedDebug') {
        this._resetSavedDebug();
      }
    });

    // One delegated listener: the switches are static, but this keeps every
    // future toggle wired for free.
    panel.addEventListener('change', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const weaponKey = target.dataset?.debug === 'equipWeapon'
        ? /** @type {HTMLSelectElement} */ (target).value
        : null;
      if (weaponKey) {
        equipWeaponOn(findPlayer(this._ctx), weaponKey, this._ctx);
        this._syncWeaponSelect();
        return;
      }
      const input = /** @type {HTMLInputElement} */ (target);
      const id = input.dataset?.toggle;
      if (id && (id === 'immortal' || id === 'pulseFrozen')) {
        // Player cheat toggles live on sandboxState.cheats.
        sandboxState.cheats[id] = input.checked;
        saveDebugPrefsFrom({ sandbox: sandboxState });
        return;
      }
      if (!id) return;
      const toggle = TOGGLES.find((candidate) => candidate.id === id);
      const system = toggle && this._ctx.world?.getSystem(toggle.system);
      if (system) toggle.set(system, input.checked);
      saveDebugPrefsFrom({ overlays: { [id]: input.checked } });
    });

    panel.addEventListener('input', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (input.dataset?.uiCopy === 'killTimerLabel') {
        this._onKillTimerLabelInput(input.value);
        return;
      }
      if (input.dataset?.playerParam === 'killTimerMax') {
        this._onPulseMaxInput(input.value);
        return;
      }
      const weaponKey = input.dataset?.weaponKey;
      const weaponPath = input.dataset?.weaponPath;
      if (weaponKey && weaponPath) {
        this._onWeaponTuningInput(weaponKey, weaponPath, input.value);
        return;
      }
      const enemyKey = input.dataset?.enemyKey;
      const enemyPath = input.dataset?.enemyPath;
      if (enemyKey && enemyPath) {
        this._onEnemyTuningInput(enemyKey, enemyPath, input.value);
        return;
      }
      const cameraPath = input.dataset?.cameraPath;
      if (cameraPath) {
        this._onCameraTuningInput(cameraPath, input.value);
        return;
      }
      const id = input.dataset?.uiScale ?? input.dataset?.uiScaleNumber;
      if (!id) return;
      this._onUiScaleInput(id, input.value);
    });
  }

  /** @returns {void} @private */
  _buildUiPanel() {
    const host = this._panels.get('ui');
    if (!host) return;

    host.innerHTML = `
      <section class="debug-hub__ui-head">
        <div>
          <h4 class="debug-hub__heading">Gameplay HUD scale</h4>
          <p class="debug-hub__note">
            Global scale is capped by the viewport; component scales multiply the visible HUD parts.
          </p>
        </div>
        <div class="debug-hub__ui-readout">
          <span>Configured <b data-ui-scale-configured>--</b></span>
          <span>Effective <b data-ui-scale-effective>--</b></span>
        </div>
      </section>
      <div class="debug-hub__scale-list">
        ${HUD_SCALE_CONTROLS.map(
          (control) => `
            <label class="debug-hub__scale-row" data-ui-scale-row="${control.id}">
              <span class="debug-hub__scale-copy">
                <b>${control.label}</b>
                <em>${control.hint}</em>
              </span>
              <input type="range" min="${control.min}" max="${control.max}" step="${control.step}"
                     data-ui-scale="${control.id}" />
              <input type="number" min="${control.min}" max="${control.max}" step="${control.step}"
                     data-ui-scale-number="${control.id}" />
            </label>
          `
        ).join('')}
      </div>
      <section class="debug-hub__section">
        <h4 class="debug-hub__heading">Countdown HUD</h4>
        <label class="debug-hub__text-row">
          <span class="debug-hub__scale-copy">
            <b>Title</b>
            <em>Top-center countdown phrase.</em>
          </span>
          <input type="text" data-ui-copy="killTimerLabel" />
        </label>
      </section>
      <div class="debug-hub__ui-actions">
        <button type="button" data-ui-scale-reset>Reset HUD Scale</button>
      </div>
    `;

    host.querySelector('[data-ui-scale-reset]')?.addEventListener('click', () => {
      this._hud?.resetScales();
      this._persistUiScales();
      this._syncUiScales();
      this._syncUiCopy();
    });
    this._syncUiScales();
    this._syncUiCopy();
    this._syncPulseMax();
  }

  /** @returns {void} @private */
  _buildCameraTuningPanel() {
    const host = this._panels.get('camera');
    if (!host || this._cameraBuilt) return;

    const { fields } = inspectCameraTuning();
    host.innerHTML = `
      <section class="debug-hub__ui-head">
        <div>
          <h4 class="debug-hub__heading">Camera parameters</h4>
          <p class="debug-hub__note">Offset is relative to the normal camera position; 0,0,0 keeps the authored rig.</p>
        </div>
      </section>
      <div class="debug-hub__camera-grid">
        ${fields
          .map(
            (field) => `
              <label class="debug-hub__camera-field">
                <span>${field.label}</span>
                <input type="number" data-camera-path="${field.path}"
                       step="${field.step}" ${numericAttr('min', field.min)} ${numericAttr('max', field.max)} />
              </label>`
          )
          .join('')}
      </div>
    `;

    this._cameraInputs.clear();
    for (const node of host.querySelectorAll('input[data-camera-path]')) {
      const input = /** @type {HTMLInputElement} */ (node);
      this._cameraInputs.set(input.dataset.cameraPath, input);
    }
    this._cameraBuilt = true;
    this._syncCameraTuning();
  }

  /** @returns {void} @private */
  _buildWeaponTuningPanel() {
    const host = this._panels.get('weapons');
    if (!host || this._weaponsBuilt) return;

    const rows = inspectWeaponTuning();
    host.innerHTML = `
      <section class="debug-hub__ui-head">
        <div>
          <h4 class="debug-hub__heading">Weapon parameters</h4>
          <p class="debug-hub__note">Edits apply to newly created or re-equipped weapons.</p>
        </div>
      </section>
      <div class="debug-hub__weapon-tuning">
        ${rows.map((weapon) => this._weaponTuningHtml(weapon)).join('')}
      </div>
    `;

    this._weaponInputs.clear();
    for (const node of host.querySelectorAll('input[data-weapon-key][data-weapon-path]')) {
      const input = /** @type {HTMLInputElement} */ (node);
      this._weaponInputs.set(`${input.dataset.weaponKey}:${input.dataset.weaponPath}`, input);
    }
    this._weaponsBuilt = true;
    this._syncWeaponTuning();
  }

  /**
   * @param {ReturnType<typeof inspectWeaponTuning>[number]} weapon Weapon tuning row.
   * @returns {string} HTML for one weapon section.
   * @private
   */
  _weaponTuningHtml(weapon) {
    return `
      <section class="debug-hub__weapon-group" data-weapon-group="${weapon.key}">
        <h4 class="debug-hub__weapon-heading">${weapon.name} <span>${weapon.key}</span></h4>
        <div class="debug-hub__weapon-grid">
          ${weapon.fields
            .map(
              (field) => `
                <label class="debug-hub__weapon-field">
                  <span>${field.label}</span>
                  <input type="number" data-weapon-key="${weapon.key}" data-weapon-path="${field.path}"
                         step="${field.step}" min="${field.min}" />
                </label>`
            )
            .join('')}
        </div>
      </section>
    `;
  }

  /** @returns {void} @private */
  _buildEnemyTuningPanel() {
    const host = this._panels.get('enemies');
    if (!host || this._enemyBuilt) return;

    const rows = inspectEnemyTuning();
    host.innerHTML = `
      <section class="debug-hub__ui-head">
        <div>
          <h4 class="debug-hub__heading">Enemy parameters</h4>
          <p class="debug-hub__note">Edits apply to enemies spawned from now on, not to bodies already in the level.</p>
        </div>
      </section>
      <div class="debug-hub__weapon-tuning">
        ${rows.map((enemy) => this._enemyTuningHtml(enemy)).join('')}
      </div>
    `;

    this._enemyInputs.clear();
    for (const node of host.querySelectorAll('input[data-enemy-key][data-enemy-path]')) {
      const input = /** @type {HTMLInputElement} */ (node);
      this._enemyInputs.set(`${input.dataset.enemyKey}:${input.dataset.enemyPath}`, input);
    }
    this._enemyBuilt = true;
    this._syncEnemyTuning();
  }

  /**
   * Shares the weapon tab's classes on purpose: the two tabs are the same kind
   * of surface, so they should not drift apart visually as either one grows.
   *
   * @param {ReturnType<typeof inspectEnemyTuning>[number]} enemy Enemy tuning row.
   * @returns {string} HTML for one archetype section.
   * @private
   */
  _enemyTuningHtml(enemy) {
    return `
      <section class="debug-hub__weapon-group" data-enemy-group="${enemy.key}">
        <h4 class="debug-hub__weapon-heading">${enemy.name} <span>${enemy.key}</span></h4>
        <div class="debug-hub__weapon-grid">
          ${enemy.fields
            .map(
              (field) => `
                <label class="debug-hub__weapon-field">
                  <span>${field.label}</span>
                  <input type="number" data-enemy-key="${enemy.key}" data-enemy-path="${field.path}"
                         step="${field.step}" min="${field.min}" />
                </label>`
            )
            .join('')}
        </div>
      </section>
    `;
  }

  /**
   * @param {string} id Scale id.
   * @param {string} value Requested value.
   * @returns {void}
   * @private
   */
  _onUiScaleInput(id, value) {
    if (!this._hud) return;
    this._hud.setHudScale(id, value);
    this._persistUiScales();
    this._syncUiScales();
  }

  /**
   * @param {string} value New countdown title.
   * @returns {void}
   * @private
   */
  _onKillTimerLabelInput(value) {
    this._hud?.setKillTimerLabel(value);
    this._persistUiScales();
    this._syncUiCopy();
  }

  /**
   * @param {string} value New Pulse max seconds.
   * @returns {void}
   * @private
   */
  _onPulseMaxInput(value) {
    if (!setPlayerValue('killTimerMax', value)) return;
    savePlayerTuning();
    this._applyPulseMaxToLivePlayer(getPlayerTuning().killTimerMax);
    this._syncPulseMax();
    this._refreshPlayerStats(this._ctx);
  }

  /**
   * @param {number} maxSeconds New Pulse maximum.
   * @returns {void}
   * @private
   */
  _applyPulseMaxToLivePlayer(maxSeconds) {
    const player = findPlayer(this._ctx);
    const health = player?.get(Health);
    if (!health?.usesKillTimer) return;
    health.killTimerMax = maxSeconds;
    health.killTimer = Math.min(health.killTimer, maxSeconds);
    this._ctx.events?.emit?.(Events.PLAYER_KILLTIMER_CHANGED, {
      entity: player,
      current: health.killTimer,
      max: health.killTimerMax
    });
  }

  /**
   * @param {string} key Weapon key.
   * @param {string} path Field path.
   * @param {string} value Input value.
   * @returns {void}
   * @private
   */
  _onWeaponTuningInput(key, path, value) {
    if (!setWeaponValue(key, path, value)) return;
    saveWeaponTuning();
    this._syncWeaponTuning();
  }

  /**
   * @param {string} key Enemy archetype key.
   * @param {string} path Field path.
   * @param {string} value Input value.
   * @returns {void}
   * @private
   */
  _onEnemyTuningInput(key, path, value) {
    if (!setEnemyValue(key, path, value)) return;
    saveEnemyTuning();
    this._syncEnemyTuning();
  }

  /**
   * @param {string} path Camera field path.
   * @param {string} value Input value.
   * @returns {void}
   * @private
   */
  _onCameraTuningInput(path, value) {
    if (!setCameraValue(path, value)) return;
    saveCameraTuning();
    this._ctx.cameraController?.applyTuning?.();
    this._syncCameraTuning();
  }

  /** @returns {void} Clears saved developer overrides and refreshes live controls. */
  _resetSavedDebug() {
    resetDebugPrefs();
    resetWaveTuning();
    resetPlayerTuning();
    resetWeaponTuning();
    resetCameraTuning();
    resetEnemyTuning();
    resetSandboxState();
    resetLevelLab();

    this._hud?.resetScales?.();
    this._ctx.cameraController?.applyTuning?.();
    for (const toggle of TOGGLES) {
      const system = this._ctx.world?.getSystem(toggle.system);
      if (system) toggle.set(system, false);
    }

    this._syncToggles();
    this._syncPlayerCheats();
    this._syncUiScales();
    this._syncUiCopy();
    this._syncPulseMax();
    this._syncCameraTuning();
    this._syncWeaponTuning();
    this._syncEnemyTuning();
    this._refreshPlayerStats(this._ctx);
  }

  /** @returns {void} @private */
  _syncUiScales() {
    if (!this._el) return;
    const configured = this._hud?.configuredScale ?? null;
    const effective = this._hud?.uiScale ?? null;
    const configuredNode = this._el.querySelector('[data-ui-scale-configured]');
    const effectiveNode = this._el.querySelector('[data-ui-scale-effective]');
    if (configuredNode) configuredNode.textContent = configured === null ? '--' : formatPercent(configured);
    if (effectiveNode) effectiveNode.textContent = effective === null ? '--' : formatPercent(effective);

    for (const control of HUD_SCALE_CONTROLS) {
      const value = this._hud?.getHudScale(control.id) ?? 1;
      const text = value.toFixed(control.step < 0.1 ? 2 : 2).replace(/\.?0+$/, '');
      const range = /** @type {HTMLInputElement | null} */ (
        this._el.querySelector(`[data-ui-scale="${control.id}"]`)
      );
      const number = /** @type {HTMLInputElement | null} */ (
        this._el.querySelector(`[data-ui-scale-number="${control.id}"]`)
      );
      if (range && range.value !== text) range.value = text;
      if (number && number.value !== text) number.value = text;
    }
  }

  /** @returns {void} @private */
  _syncUiCopy() {
    if (!this._el) return;
    const input = /** @type {HTMLInputElement | null} */ (
      this._el.querySelector('[data-ui-copy="killTimerLabel"]')
    );
    if (!input || input === document.activeElement) return;
    input.value = this._hud?.getKillTimerLabel?.() ?? '';
  }

  /** @returns {void} @private */
  _syncPulseMax() {
    if (!this._el) return;
    const text = String(getPlayerTuning().killTimerMax);
    for (const input of this._el.querySelectorAll('[data-player-param="killTimerMax"]')) {
      const field = /** @type {HTMLInputElement} */ (input);
      if (field !== document.activeElement && field.value !== text) field.value = text;
    }
  }

  /** @returns {void} @private */
  _syncWeaponTuning() {
    if (!this._weaponsBuilt) return;
    for (const weapon of inspectWeaponTuning()) {
      for (const field of weapon.fields) {
        const input = this._weaponInputs.get(`${weapon.key}:${field.path}`);
        if (!input || input === document.activeElement) continue;
        const text = String(round(field.value));
        if (input.value !== text) input.value = text;
        input.classList.toggle('is-overridden', field.overridden);
      }
    }
  }

  /** @returns {void} @private */
  _syncEnemyTuning() {
    if (!this._enemyBuilt) return;
    for (const enemy of inspectEnemyTuning()) {
      for (const field of enemy.fields) {
        const input = this._enemyInputs.get(`${enemy.key}:${field.path}`);
        // Never overwrite the field being typed into.
        if (!input || input === document.activeElement) continue;
        const text = String(round(field.value));
        if (input.value !== text) input.value = text;
        input.classList.toggle('is-overridden', field.overridden);
      }
    }
  }

  /** @returns {void} @private */
  _syncCameraTuning() {
    if (!this._cameraBuilt) return;
    for (const field of inspectCameraTuning().fields) {
      const input = this._cameraInputs.get(field.path);
      if (!input || input === document.activeElement) continue;
      const text = String(round(field.value));
      if (input.value !== text) input.value = text;
      input.classList.toggle('is-overridden', field.overridden);
    }
  }

  /** @returns {void} @private */
  _persistUiScales() {
    if (!this._hud) return;
    saveDebugPrefsFrom({ ui: this._hud.snapshotScales() });
  }

  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _refreshPlayerStats(ctx) {
    if (!this._el) return;

    const player = findPlayer(ctx);
    if (!player) {
      this._setPlayerStat('moveSpeed', '--');
      this._setPlayerStat('pulse', 'no player');
      this._setPlayerStat('iframes', '--');
      this._setPlayerStat('shield', '--');
      this._setPlayerStat('position', '--');
      return;
    }

    const control = player.get(PlayerControlled);
    const health = player.get(Health);
    const velocity = player.get(Velocity);
    const effects = player.get(StatusEffects);
    const transform = player.get(Transform);

    const speed = velocity ? Math.hypot(velocity.linear.x, velocity.linear.z) : 0;
    const cap = velocity?.maxSpeed ?? 0;
    this._setPlayerStat('moveSpeed', `${formatNumber(speed)} / ${formatNumber(cap)} u/s`);

    if (health?.usesKillTimer) {
      const ratio = health.killTimerMax > 0 ? health.killTimer / health.killTimerMax : 0;
      this._setPlayerStat(
        'pulse',
        `${formatNumber(health.killTimer)}s / ${formatNumber(health.killTimerMax)}s (${Math.round(ratio * 100)}%)`
      );
    } else {
      this._setPlayerStat('pulse', '--');
    }

    this._setPlayerStat(
      'iframes',
      health && health.invulnerableFor > 0 ? `${formatNumber(health.invulnerableFor, 2)}s` : 'ready'
    );
    this._setPlayerStat(
      'shield',
      effects && effects.shield > 0
        ? `${formatNumber(effects.shield)} / ${formatNumber(effects.shieldRemaining, 1)}s`
        : 'none'
    );
    this._setPlayerStat(
      'position',
      transform
        ? `${formatNumber(transform.position.x)}, ${formatNumber(transform.position.z)}`
        : '--'
    );
  }

  /**
   * @param {string} id Stat id.
   * @param {string} value Display value.
   * @returns {void}
   * @private
   */
  _setPlayerStat(id, value) {
    const node = this._el?.querySelector(`[data-dbg-stat="${id}"]`);
    if (node && node.textContent !== value) node.textContent = value;
  }

  /**
   * Reflects cheat toggles into the DOM after the window opens.
   * @private
   */
  _syncPlayerCheats() {
    if (!this._el) return;
    const immortalInput = /** @type {HTMLInputElement | null} */ (
      this._el.querySelector('[data-toggle="immortal"]')
    );
    const pulseInput = /** @type {HTMLInputElement | null} */ (
      this._el.querySelector('[data-toggle="pulseFrozen"]')
    );
    if (immortalInput) immortalInput.checked = !!sandboxState.cheats.immortal;
    if (pulseInput) pulseInput.checked = !!sandboxState.cheats.pulseFrozen;
  }

  /**
   * Populates the weapon equip dropdown with player-usable weapons.
   * @private
   */
  _buildWeaponSelect() {
    const select = /** @type {HTMLSelectElement | null} */ (
      this._el?.querySelector('[data-debug="equipWeapon"]')
    );
    if (!select) return;
    select.innerHTML = '';
    for (const weapon of inspectWeaponTuning()) {
      const opt = document.createElement('option');
      opt.value = weapon.key;
      opt.textContent = weapon.name;
      select.appendChild(opt);
    }
  }

  /** @returns {void} @private */
  _syncWeaponSelect() {
    const select = /** @type {HTMLSelectElement | null} */ (
      this._el?.querySelector('[data-debug="equipWeapon"]')
    );
    if (!select) return;

    const key = findPlayer(this._ctx)?.get(Weapon)?.key ?? '';
    if (key && Array.from(select.options).some((option) => option.value === key)) {
      select.value = key;
    } else {
      select.value = '';
    }
  }

  /** @returns {void} @private */
  _syncRunActions() {
    const button = /** @type {HTMLButtonElement | null} */ (
      this._el?.querySelector('[data-debug="restartLevel"]')
    );
    if (!button) return;
    button.disabled = !this._onRestartLevel || (this._canRestartLevel ? !this._canRestartLevel() : false);
  }

  /**
   * @param {string} id Tab id.
   * @returns {void}
   * @private
   */
  _selectTab(id) {
    if (!this._panels.has(id)) id = 'overlays';
    this._active = id;
    // Persist only interactive selections: _build runs a default select while
    // the window is still closed, and that must never clobber the saved tab.
    if (this._open) saveDebugPrefsFrom({ hub: { open: true, tab: id } });

    for (const [tabId, tab] of this._tabs) {
      tab.classList.toggle('debug-hub__tab--active', tabId === id);
    }
    for (const [panelId, panel] of this._panels) {
      panel.hidden = panelId !== id;
    }

    // The hosted tools each have their own idea of "visible"; a tab switch is
    // the only place that has to reconcile them.
    if (this._waveDebug) id === 'waves' ? this._waveDebug.open() : this._waveDebug.close();
    this._sandbox?.setEnabled(id === 'sandbox');
    this._levelLab?.setVisible(id === 'levels');
    if (id === 'camera') this._syncCameraTuning();
    if (id === 'weapons') this._syncWeaponTuning();
    if (id === 'enemies') this._syncEnemyTuning();
  }

  /** @returns {void} Reflects the systems' real state into the checkboxes. @private */
  _syncToggles() {
    for (const toggle of TOGGLES) {
      const input = /** @type {HTMLInputElement | null} */ (
        this._el.querySelector(`[data-toggle="${toggle.id}"]`)
      );
      const system = this._ctx.world?.getSystem(toggle.system);
      if (input && system) input.checked = toggle.get(system);
    }
  }

  /** @returns {void} Removes the window from the document. */
  dispose() {
    this._levelLab?.dispose();
    this._postFx?.dispose();
    this._el?.remove();
    this._el = null;
    this._panels.clear();
    this._tabs.clear();
    this._cameraInputs.clear();
    this._weaponInputs.clear();
    this._enemyInputs.clear();
    this._cameraBuilt = false;
    this._weaponsBuilt = false;
    this._enemyBuilt = false;
    this._active = '';
    this._open = false;
  }
}

/**
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {import('../ecs/Entity.js').Entity | null} Current player entity.
 */
function findPlayer(ctx) {
  const activePlayer = /** @type {import('../ecs/Entity.js').Entity | null} */ (ctx.player ?? null);
  if (activePlayer?.alive && activePlayer.has?.(PlayerControlled)) return activePlayer;

  const query = ctx.world?.query;
  if (typeof query !== 'function') return null;

  return query.call(ctx.world, PlayerControlled)[0] ?? null;
}

/**
 * @param {number} value Number to display.
 * @param {number} [decimals] Digits after the decimal point.
 * @returns {string} Compact fixed number.
 */
function formatNumber(value, decimals = 1) {
  if (!Number.isFinite(value)) return '--';
  return value.toFixed(decimals);
}

/**
 * @param {number} value Scale multiplier.
 * @returns {string} Percentage label.
 */
function formatPercent(value) {
  if (!Number.isFinite(value)) return '--';
  return `${Math.round(value * 100)}%`;
}

/**
 * @param {number} value Numeric editor value.
 * @returns {number} Compact value that avoids long floating point tails.
 */
function round(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * @param {string} name Attribute name.
 * @param {number | undefined} value Attribute value.
 * @returns {string} Attribute markup or an empty string.
 */
function numericAttr(name, value) {
  return value === undefined ? '' : `${name}="${String(value)}"`;
}
